import { useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import { Button, Flex, Spin } from "antd"
import { CalendarOutlined, EnvironmentOutlined, LeftOutlined } from "@ant-design/icons"
import { useNavigate, useParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { routes } from "router"
import {
  fetchUpcomingExperienceDetail,
  selectUpcomingExperienceDetail,
  selectUpcomingExperienceDetailLoading
} from "store/upcomingExperiences"
import BookingTag from "../BookingTag"
import BookingDetailSummary from "./BookingDetailSummary"
import ReservationsTable from "./ReservationsTable"
import { STATUS_TAG_COLOR } from "./constants"
import styles from "./index.module.css"

const BookingStatusTag = ({ status }) => {
  const normalized = status?.toLowerCase()
  const color = STATUS_TAG_COLOR[normalized] || "blue"
  return <BookingTag color={color}>{status}</BookingTag>
}

BookingStatusTag.propTypes = {
  status: PropTypes.string
}

const BookingDetailsPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { id } = useParams()
  const data = useSelector(selectUpcomingExperienceDetail)
  const loading = useSelector(selectUpcomingExperienceDetailLoading)

  const refreshDetail = useCallback(() => {
    if (id) dispatch(fetchUpcomingExperienceDetail(id))
  }, [id, dispatch])

  useEffect(() => {
    refreshDetail()
  }, [refreshDetail])

  if (loading || !data) {
    return (
      <Flex align="center" justify="center" className={styles.page} style={{ minHeight: 240 }}>
        <Spin />
      </Flex>
    )
  }

  const title = data.experienceTitle
  const experienceDate = data.experienceDate
  const startTime = data.experienceTimeRange?.start_time
  const endTime = data.experienceTimeRange?.end_time
  const status = data.bookingStatus
  const address = data.address

  return (
    <Flex vertical gap={16} className={styles.page}>
      {/* Breadcrumb */}
      <Flex align="center" gap={6} className={styles.breadcrumb}>
        <button type="button" className={styles.breadcrumbItem} onClick={() => navigate(routes.guideUpcomingExperiencesPath())}>
          {t("pro.sidebar.menu.bookings_by_experience", "By Experience")}
        </button>
        <span className={styles.breadcrumbSep}>·</span>
        <span className={styles.breadcrumbCurrent}>#{data.bookingId}</span>
      </Flex>

      {/* Page header */}
      <Flex gap={8} align="flex-start" className={styles.pageHeader}>
        <Button type="text" icon={<LeftOutlined />} className={styles.titleBackBtn} onClick={() => navigate(-1)} />
        <Flex vertical gap={2}>
          <span className={styles.pageTitle}>{title}</span>
          <Flex align="center" gap={6} className="w-max">
            <CalendarOutlined className={styles.metaIcon} />
            <span className={styles.metaText}>
              {experienceDate}
              {startTime && ` | ${[startTime, endTime].filter(Boolean).join(" - ")}`}
            </span>
            {status && <BookingStatusTag status={status} />}
          </Flex>
          {address && (
            <Flex align="center" gap={4}>
              <EnvironmentOutlined className={styles.metaIcon} />
              <span className={styles.metaText}>{address}</span>
            </Flex>
          )}
        </Flex>
      </Flex>

      {/* Split-panel body */}
      <Flex gap={16} align="flex-start" className={styles.body}>
        <Flex vertical gap={12} className={styles.leftPanel}>
          <BookingDetailSummary
            bookingId={data.bookingUuid || id}
            guestCount={data.capacitySummary?.guestsBooked}
            maxGuests={data.capacitySummary?.capacity}
            reservationCount={data.capacitySummary?.reservationCount}
            experienceType={data.experienceType}
            received={data.paymentSummary?.received}
            outstanding={data.paymentSummary?.outstanding}
            totalBooked={data.paymentSummary?.totalBooked}
            potentialRevenue={data.paymentSummary?.potentialRevenue}
            initialCrew={data.crew}
            initialEquipment={data.resources}
            notes={data.notes}
          />
        </Flex>
        <div className={styles.rightPanel}>
          <ReservationsTable reservations={data.reservations} experienceType={data.experienceType} onRefresh={refreshDetail} />
        </div>
      </Flex>
    </Flex>
  )
}

export default BookingDetailsPage
